import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Plus, 
  Search, 
  Filter, 
  ChevronDown, 
  ArrowUpDown,
  MoreHorizontal,
  Edit,
  Play,
  X,
  Check,
  Clock,
  XCircle,
  Briefcase,
  Users,
  FileText,
  Calendar,
  DollarSign
} from "lucide-react";
import { useToast } from "../components/shared/ToastProvider";
import { useButtonClick } from "../hooks/useInteractiveElement";
import JobCreationModal from "../components/JobCreationModal";
import { getJobStatusColor, getJobStatusLabel, getAllJobStatusOptions } from "../utils/jobStatusHelpers";
import ClientSubmissionModal from "../components/ClientSubmissionModal";
import AddSubmissionModal from "../components/AddSubmissionModal";
import { useNavigate } from "react-router-dom";
import { formatJobId } from "../utils/idFormatter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
const API = `${BACKEND_URL}/api`;

const JobManagement = () => {
  const { showToast } = useToast();
  const { handleClick, isLoading } = useButtonClick();
  const navigate = useNavigate();
  
  // Get current user to check role
  const [currentUser, setCurrentUser] = useState(null);
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showScrapingModal, setShowScrapingModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [scrapingInProgress, setScrapingInProgress] = useState(false);
  
  // Table controls
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [openActionMenus, setOpenActionMenus] = useState({});
  const [showClientSubmissionModal, setShowClientSubmissionModal] = useState(false);
  const [showAddSubmissionModal, setShowAddSubmissionModal] = useState(false);
  const [selectedJobForSubmission, setSelectedJobForSubmission] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  // Close action menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setOpenActionMenus({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get current user from localStorage
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        setCurrentUser(JSON.parse(user));
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
      showToast('Jobs loaded successfully');
    } catch (error) {
      console.error("Error fetching jobs:", error);
      showToast('Failed to load jobs', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Check if current user is a recruiter (has permission to submit candidates)
  const isRecruiter = () => {
    return currentUser?.role === 'recruiter' || currentUser?.user_type === 'recruiter';
  };

  const handleCreateJob = async (jobData) => {
    return handleClick(async () => {
      await axios.post(`${API}/jobs`, jobData);
      fetchJobs();
      setShowCreateModal(false);
    }, {
      loading: 'Creating job...',
      success: `Job "${jobData.title}" created successfully!`,
      error: 'Failed to create job. Please try again.'
    });
  };

  const handleUpdateJobStatus = async (jobId, newStatus) => {
    const job = jobs.find(j => j.id === jobId);
    return handleClick(async () => {
      await axios.put(`${API}/jobs/${jobId}`, { status: newStatus });
      fetchJobs();
    }, {
      loading: `Updating job status...`,
      success: `Job "${job?.title || 'Unknown'}" status updated to ${newStatus}`,
      error: 'Failed to update job status'
    });
  };

  const handleStartScraping = async (jobId, source) => {
    const job = jobs.find(j => j.id === jobId);
    return handleClick(async () => {
      setScrapingInProgress(true);
      const searchQuery = `${job.title} ${job.required_skills.slice(0, 3).join(' ')}`;
      
      const response = await axios.post(`${API}/scraping/start`, {
        job_requisition_id: jobId,
        source: source,
        search_query: searchQuery,
        location: job.location
      });
      
      setShowScrapingModal(false);
      setSelectedJob(null);
      setScrapingInProgress(false);
      return response.data;
    }, {
      loading: `Sourcing candidates from ${source}...`,
      success: `Successfully sourced candidates from ${source}!`,
      error: `Failed to source candidates from ${source}`
    });
  };

  // Helper functions for table - now using centralized utility
  // (getJobStatusColor and getJobStatusLabel imported from utils)

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort jobs
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = (job.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (job.department || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (job.location || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    const matchesDepartment = departmentFilter === 'all' || job.department === departmentFilter;
    
    return matchesSearch && matchesStatus && matchesDepartment;
  });

  const sortedJobs = [...filteredJobs].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    if (sortField === 'created_at' || sortField === 'updated_at') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedJobs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedJobs = sortedJobs.slice(startIndex, startIndex + itemsPerPage);

  const handleClientSubmissionFromTable = async (submissionData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${submissionData.jobId}/client-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      if (response.ok) {
        showToast(`Successfully submitted ${submissionData.candidateIds.length} candidate(s) to client for ${selectedJobForSubmission?.title}`, 'success');
      } else {
        showToast('Failed to submit candidates to client', 'error');
      }
    } catch (error) {
      console.error('Error submitting to client:', error);
      showToast('Error submitting candidates to client', 'error');
    } finally {
      setShowClientSubmissionModal(false);
      setSelectedJobForSubmission(null);
    }
  };

  const handleSubmissionCreatedFromTable = (newSubmission) => {
    showToast('Submission created successfully!', 'success');
    setShowAddSubmissionModal(false);
    setSelectedJobForSubmission(null);
  };

  // Get unique departments for filter
  const uniqueDepartments = [...new Set(jobs.map(job => job.department))];

  // Toggle actions menu for specific job
  const toggleActionMenu = (jobId) => {
    setOpenActionMenus(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  // State for editing
  const [editingJob, setEditingJob] = useState(null);

  // Handle action clicks
  const handleTableActionClick = (jobId, actionType) => {
    setOpenActionMenus(prev => ({ ...prev, [jobId]: false }));
    const selectedJobData = jobs.find(j => j.id === jobId);
    
    switch(actionType) {
      case 'view':
        navigate(`/jobs/${jobId}`);
        showToast('Navigated to job details', 'success');
        break;
      case 'edit':
        setEditingJob(selectedJobData);
        setShowCreateModal(true);
        break;
      case 'prescreen':
        setSelectedJobForSubmission(selectedJobData);
        setShowAddSubmissionModal(true);
        break;
      case 'submissions':
        navigate(`/jobs/${jobId}`);
        setTimeout(() => {
          showToast('Navigated to job submissions', 'success');
        }, 100);
        break;
      case 'client_submission':
        setSelectedJobForSubmission(selectedJobData);
        setShowClientSubmissionModal(true);
        break;
      case 'interview':
        showToast('Interview scheduling functionality coming soon', 'info');
        break;
      case 'offer_made':
        showToast('Offer extended functionality coming soon', 'info');
        break;
      case 'offer_accepted':
        showToast('Offer accepted functionality coming soon', 'info');
        break;
      default:
        showToast('Action not implemented', 'warning');
    }
  };

  return (
    <div className="space-y-6" data-testid="job-management-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Job Opportunities</h1>
          <p className="text-gray-600 mt-1">Manage your open positions and track progress</p>
        </div>
        <button
          onClick={() => {
            setShowCreateModal(true);
            showToast('Opening job creation form');
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2 shadow-lg"
          data-testid="create-job-button"
        >
          <Plus className="h-5 w-5" />
          <span>Create New Job</span>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Statuses</option>
            {getAllJobStatusOptions().map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            {/* Legacy statuses for backwards compatibility */}
            <option value="draft">Draft (Legacy)</option>
            <option value="open">Open (Legacy)</option>
            <option value="closed">Closed (Legacy)</option>
            <option value="on-hold">On Hold (Legacy)</option>
          </select>

          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Departments</option>
            {uniqueDepartments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>

          <div className="flex items-center space-x-2">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">{sortedJobs.length} jobs</span>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : sortedJobs.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Briefcase className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No jobs found</h3>
          <p className="text-gray-600 mb-6">
            {jobs.length === 0 
              ? "Create your first job requisition to start sourcing candidates"
              : "No jobs match your current filters"
            }
          </p>
          <button
            onClick={() => {
              if (jobs.length === 0) {
                setShowCreateModal(true);
                showToast('Opening job creation form');
              } else {
                setSearchTerm('');
                setStatusFilter('all');
                setDepartmentFilter('all');
                showToast('Filters cleared');
              }
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{jobs.length === 0 ? 'Create First Job' : 'Clear Filters'}</span>
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedJobs.map((job) => (
                  <tr 
                    key={job.id} 
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleTableActionClick(job.id, 'view')}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Briefcase className="h-5 w-5 text-purple-600 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{job.title}</div>
                          {job.department && (
                            <div className="text-xs text-gray-500">{job.department}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {job.company || job.client_name || job.client || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.location || 'Remote'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {job.employment_type || 'Full-time'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getJobStatusColor(job.status)}`}>
                        {getJobStatusLabel(job.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(job.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleTableActionClick(job.id, 'view')}
                          className="text-purple-600 hover:text-purple-900 flex items-center gap-1"
                          title="View Details"
                        >
                          <FileText className="h-4 w-4" />
                          View
                        </button>
                        <button
                          onClick={() => handleTableActionClick(job.id, 'edit')}
                          className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                          title="Edit Job"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                ‹
              </button>
              <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded">
                {currentPage}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unified Create/Edit Job Modal */}
      <JobCreationModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingJob(null);
        }}
        editingJob={editingJob}
        onSuccess={(result) => {
          showToast(`Job "${result.title}" ${editingJob ? 'updated' : 'created'} successfully!`);
          fetchJobs(); // Refresh the job list
          setEditingJob(null);
        }}
      />

      {/* Client Submission Modal */}
      {showClientSubmissionModal && selectedJobForSubmission && (
        <ClientSubmissionModal
          isOpen={showClientSubmissionModal}
          onClose={() => {
            setShowClientSubmissionModal(false);
            setSelectedJobForSubmission(null);
          }}
          onSubmit={handleClientSubmissionFromTable}
          job={selectedJobForSubmission}
        />
      )}

      {/* Add Submission Modal */}
      {showAddSubmissionModal && selectedJobForSubmission && (
        <AddSubmissionModal
          isOpen={showAddSubmissionModal}
          onClose={() => {
            setShowAddSubmissionModal(false);
            setSelectedJobForSubmission(null);
          }}
          selectedJob={selectedJobForSubmission}
          onSubmissionCreated={handleSubmissionCreatedFromTable}
        />
      )}

      {/* Scraping Modal */}
      {showScrapingModal && selectedJob && (
        <ScrapingModal
          job={selectedJob}
          onClose={() => {
            setShowScrapingModal(false);
            setSelectedJob(null);
          }}
          onStart={handleStartScraping}
          inProgress={scrapingInProgress}
        />
      )}

    </div>
  );
};

// CreateJobModal component removed - replaced by EnhancedJobCreateModal

const ScrapingModal = ({ job, onClose, onStart, inProgress }) => {
  const [selectedSources, setSelectedSources] = useState([]);

  const sources = [
    { id: "linkedin", name: "LinkedIn", color: "bg-blue-600", icon: "💼" },
    { id: "indeed", name: "Indeed", color: "bg-blue-700", icon: "🔍" },
    { id: "monster", name: "Monster", color: "bg-purple-600", icon: "👾" },
    { id: "all", name: "All Sources", color: "bg-green-600", icon: "🌐" }
  ];

  const toggleSource = (sourceId) => {
    if (sourceId === "all") {
      setSelectedSources(["all"]);
    } else {
      setSelectedSources(prev => 
        prev.includes(sourceId) 
          ? prev.filter(s => s !== sourceId)
          : [...prev.filter(s => s !== "all"), sourceId]
      );
    }
  };

  const handleStart = () => {
    const source = selectedSources.includes("all") ? "all" : selectedSources[0];
    if (source) {
      onStart(job.id, source);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Source Candidates</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Job Position:</p>
            <p className="font-semibold text-gray-900">{job.title}</p>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">Select Source:</p>
            <div className="grid grid-cols-2 gap-3">
              {sources.map((source) => (
                <button
                  key={source.id}
                  onClick={() => toggleSource(source.id)}
                  disabled={inProgress}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedSources.includes(source.id)
                      ? `${source.color} text-white border-transparent`
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  } ${inProgress ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="text-2xl mb-1">{source.icon}</div>
                  <div className="font-medium text-sm">{source.name}</div>
                </button>
              ))}
            </div>
          </div>

          {selectedSources.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                We'll search for candidates matching "{job.title}" in {job.location}
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={inProgress}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            disabled={selectedSources.length === 0 || inProgress}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {inProgress ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sourcing...</span>
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                <span>Start Sourcing</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobManagement;